<template>
  <ViewFilterComponentV2
    v-if="filterV2"
    :source="source"
    :filters-options="filtersOptions"
    :filter-data="filterData"
    :show-clear-all="showClearAll"
    :show-export-option="showExportOption"
    :current-view="currentView"
    :total-size="totalSize"
    @filter-changed="handleFilterChange"
    @clear-all-filters="clearAllFilters"
    @filter-search="handleDirectSearch"
  />
  <div
    v-else
    :class="['items-center flex flex-row justify-between pr-2', extraStyles]"
  >
    <div class="flex flex-row items-center justify-between w-full">
      <div class="flex flex-row flex-wrap gap-2">
        <template v-for="filter in visibleFilters" :key="filter.id">
          <FilterDropdownItem
            :filter="filter"
            :is-open="isFilterOpen(filter)"
            :selected-count="getFilterSelectedCount(filter)"
            :is-filter-applied="isFilterApplied(filter)"
            :selected-names="getSelectedNames(filter)"
            :title="getFilterTitle(filter)"
            :ondelete="handleDeleteFilter"
            @toggle="toggleDropdown"
            @clear="clearAllSelections"
          >
            <div class="flex items-center justify-between p-3 pt-1">
              <span class="text-sm font-semibold text_color">{{ filter.name }}</span>
              <ThemeImage
                :icon="imageNames.DUSTBIN_GREY"
                alt="delete_icon"
                class="cursor-pointer size-4"
                @click.stop="handleDeleteFilter(filter)"
              />
            </div>
            
            <CustomDateFilter
              v-if="showCustomDateFilter && dateFilterTypes.includes(filter.id)"
              :source="openDropdownId"
              :parent-format="dateTimeFormats[3]"
              :start-date="startDate"
              :end-date="endDate"
              @update:customDateChange="handleDateChange"
              @goBack="handleBackButton"
            />
            <OpenedFilter
              v-else-if="filter.id === 'minimum_opened_count'"
              :source="openDropdownId"
              :current-value="filterData[filter.id]"
              @update:openedFilterChange="handleOpenedFilterChange"
            />
            <TabbedFilter
              v-else-if="filter.withTabs"
              :filter="filter"
              :filter-data="filterData"
              @select-option="handleTabbedSelect"
              @search="handleDirectSearch"
            />
            <FilterOptionsList
              v-else
              :filter-id="filter.id"
              :filter-name="filter.name"
              :options="getFilteredOptions(filter)"
              :searchable="filter.searchable"
              :show-initials="filter.showInitials"
              :is-date="dateFilterTypes.includes(filter.id)"
              :has-more-options="filter.hasMoreOptions || false"
              :is-loading="filter.isLoading"
              :allow-custom-input="
                source === entityTypes.COLLEAGUES ||
                source === entityTypes.EMPLOYEES ||
                filter.allowCustomInput
              "
              @select-option="(option) => handleOptionClick(option, filter)"
              @search="handleDirectSearch"
              @custom-date="handleCustomDate"
              @load-more="handleLoadMore"
            />
          </FilterDropdownItem>
        </template>
      </div>

      <span
        v-if="showClearAll && isAnyFilterApplied"
        class="cursor-pointer text_color text-sm font-semibold dropdown_item !w-auto mr-2"
        @click="clearAllFilters"
      >
        Reset
      </span>
    </div>

    <CustomDropdown
      v-if="showExportOption"
      maxHeight="200px"
      gap="10px"
      position="end"
    >
      <template #trigger>
        <div
          class="flex items-center justify-center p-2 rounded-lg bg-[--white-opacity-2] border_8 cursor-pointer hover:bg-[--white-opacity-4] hover:border_12"
        >
          <ThemeImage
            class="size-4 cursor-pointer"
            :icon="imageNames.MORE_VERTICAL"
            alt="deals_icon"
          />
        </div>
      </template>

      <template #default>
        <div
          v-for="item in threeDotsDropdownItems"
          :key="item.key"
          class="flex items-center flex-row gap-2 px-3 py-2 rounded-lg"
          @click="item.action"
          :class="[
            item.key === 'export' && isExportDisabled
              ? 'cursor-not-allowed hover:bg-transparent'
              : 'dropdown_item',
          ]"
        >
          <ThemeImage class="size-4" :icon="item.icon" alt="dropdown_icons" />
          <span
            class="proxima_nova text-sm"
            :class="{
              secondary_color: item.key === 'export' && isExportDisabled,
            }"
            >{{ item.text }}</span
          >
        </div>
      </template>
    </CustomDropdown>

    <CustomModal
      title="Export View"
      v-model:visible="showExportViewModal"
      :width="'32vw'"
    >
      <div class="flex flex-col gap-6">
        <span>{{
          `'${currentView?.name}' view will be exported to your email`
        }}</span>
        <div id="email" class="form_col">
          <span class="secondary_color text-[13px]">Email ID</span>
          <CustomInput
            format="text"
            v-model="emailToSendExport"
            placeholder="Enter Email"
            type="primary"
            :is-error="emailToSendExportError !== ''"
            :error-message="emailToSendExportError"
            :on-change="() => (emailToSendExportError = '')"
          />

          <span v-if="totalSize > 25000" class="text-red-500 text-[13px]"
            >Selection exceeds maximum limit of 25,000 rows
          </span>
        </div>
      </div>

      <template #footer>
        <a-button
          class="primary_medium"
          @click="exportWithEmailList"
          :disabled="totalSize > 25000"
          >Export</a-button
        >
      </template>
    </CustomModal>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";

import debounce from "lodash/debounce.js";
import CustomInput from "@/src/components/CustomInput.vue";
import { showExportMessage, validateEmail } from "@/src/utils/utils.js";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import CustomModal from "@/src/components/CustomModal.vue";
import CustomDateFilter from "@/src/components/CustomDateFilter.vue";
import OpenedFilter from "./OpenedFilter.vue";
import DashboardService from "@/src/api/DashboardService.js";
import CRMService from "@/src/api/CRMService.js";
import { message } from "ant-design-vue";
import { useRouter } from "vue-router";
import OutreachService from "@/src/api/OutreachService.js";
import SequenceService from "@/src/api/SequenceService.js";
import dayjs from "dayjs";
import { formatISODate } from "@/src/utils/dateTimeUtils.js";
import { dateTimeFormats } from "@/src/constants/dateTimeConstants.js";
import FilterDropdownItem from "./FilterDropdownItem.vue";
import FilterOptionsList from "./FilterOptionsList.vue";
import TabbedFilter from "./TabbedFilter.vue";
import ViewFilterComponentV2 from "./ViewFilterComponentV2.vue";
import store from "@/src/store/index.js";
import {
  exportEmailNotRequired,
  exportEmailNotRequiredFunctions,
} from "@/src/constants/filterConstants.js";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import { downloadCSVFile } from "@/src/helpers/downloadHelper.js";
import {
  isSchemaEntityType,
  dateFilterTypes,
  searchableFilterIds,
} from "@/src/constants/crmConstants.js";
import { transformCRMSearchResult } from "@/src/utils/crmFilterUtils.js";

import { imageNames } from "@/src/constants/imageConstants.js";

const props = defineProps({
  source: { type: String, required: false },
  filtersOptions: { type: Array, required: true },
  filterData: { type: Object, required: true },
  showClearAll: { type: Boolean, default: true },
  entireData: { type: Boolean, default: false },
  showExportOption: { type: Boolean, default: false },
  pageParams: { type: Object, default: () => ({}) },
  currentView: { type: Object, default: null },
  totalSize: { type: Number, default: 10 },
  extraStyles: { type: String, default: "flex-grow" },
});

const filterV2 = computed(() => {
  return isValidSchemaEntity.value;
});

const emit = defineEmits([
  "filter-changed",
  "clear-all-filters",
  "filter-search",
  "load-more-options",
]);

const router = useRouter();
const openDropdownId = ref(null);
const emailToSendExport = ref("");
const emailToSendExportError = ref("");
const showExportViewModal = ref(false);
const startDate = ref(dayjs());
const endDate = ref(dayjs());
const showCustomDateFilter = ref(false);
const isCustomDate = ref(false);
const exporting = ref(false);
const isValidSchemaEntity = ref(false);
const hasExportAccess = computed(() => store.getters["rbac/hasExportAccess"]);
const isExportDisabled = computed(() => {
  return props.totalSize === 0 || !hasExportAccess.value;
});

const emailNotRequired = computed(() => {
  return exportEmailNotRequired.includes(props.source);
});

const visibleFilters = computed(() => {
  const tabFilterIds = new Set();

  props.filtersOptions.forEach((filter) => {
    if (filter.withTabs && Array.isArray(filter.tabs)) {
      filter.tabs.forEach((tab) => {
        tabFilterIds.add(tab.id);
      });
    }
  });

  return props.filtersOptions.filter((filter) => !tabFilterIds.has(filter.id));
});

const isFilterOpen = (filter) => {
  if (filter.withTabs) {
    return filter.tabs.some((tab) => tab.id === openDropdownId.value);
  }
  return openDropdownId.value === filter.id;
};

const getFilterSelectedCount = (filter) => {
  if (filter.withTabs) {
    return filter.tabs.reduce((count, tab) => {
      const selectedIds = props.filterData[tab.id];
      return (
        count +
        (Array.isArray(selectedIds) ? selectedIds.length : selectedIds ? 1 : 0)
      );
    }, 0);
  }

  const selectedIds = props.filterData[filter.id];
  return Array.isArray(selectedIds) ? selectedIds.length : selectedIds ? 1 : 0;
};

const handleTabbedSelect = (option, tab) => {
  selectOption(option, tab);
};

const threeDotsDropdownItems = [
  {
    key: "export",
    action: () => handleOpenExportModal(),
    icon: imageNames.EXPORT_GREY,
    text: `${emailNotRequired ? "Export CSV" : "Export view"}`,
  },
];

const optionTransformers = {
  industry: (item) => ({
    id: item,
    name: item,
  }),
  default: (item) => ({
    id: item.id,
    name: item.name,
    ...(item.isDeleted ? { isDeleted: true } : {}),
  }),
};

const isAnyFilterApplied = computed(() => {
  return Object.entries(props.filterData).some(
    ([key, value]) =>
      key !== "pipeline_id" &&
      value !== null &&
      value !== undefined &&
      value !== "" &&
      (!Array.isArray(value) || value.length > 0),
  );
});

const handleOpenExportModal = () => {
  if (isExportDisabled.value) {
    return;
  }

  if (emailNotRequired.value) {
    exportWithoutEmailList();
  } else {
    emailToSendExportError.value = "";
    emailToSendExport.value = localStorage.getItem("userEmail") || "";
    showExportViewModal.value = true;
  }
};

const clearAllFilters = () => {
  const clearedFilters = Object.fromEntries(
    Object.entries(props.filterData).filter(([key]) => key === "pipeline_id"),
  );
  emit("clear-all-filters", clearedFilters);
};

const handleFilterSearch = async (filterId, query) => {
  let targetFilter = props.filtersOptions.find((f) => f.id === filterId);
  if (!targetFilter) {
    for (const f of props.filtersOptions) {
      if (f.withTabs) {
        const tab = f.tabs.find((t) => t.id === filterId);
        if (tab) {
          targetFilter = tab;
          break;
        }
      }
    }
  }

  if (targetFilter) {
    targetFilter.isLoading = true;
  }

  try {
    let response;
    if (filterId === "company" || filterId === "deal_company") {
      response = await CRMService.searchDynamicObjects({
        object_type: 'companies',
        search_query: query || '',
        page: 1,
        page_size: 50,
      });
      if (response?.success) {
        response.companies = (response.results || []).map((company) => {
          const attrs = company?.attributes || {};
          return {
            id: parseInt(company?.id, 10),
            name: attrs?.name?.value || attrs?.name || null,
          };
        });
      }
    } else if (filterId === "contact") {
      response = await CRMService.searchDynamicObjects({
        object_type: 'contacts',
        search_query: query || '',
        page: 1,
        page_size: 50,
      });
      if (response?.success) {
        response.contacts = (response.results || []).map((contact) => transformCRMSearchResult(contact));
      }
    } else if (filterId === "industry") {
      response = await OutreachService.campaignFilters("industry", query);
    } else if (filterId === "sequences" || filterId === "sequence_id") {
      response = await SequenceService.fetchSequences(0, 20, query);
    } else if (filterId === "linkedin_integration_id") {
      response = await DashboardService.fetchAllLinkedInIntegrations();
      if (response?.success) {
        response.options = response.linkedin_accounts
          .filter((account) => account.status === "CONNECTED")
          .filter(
            (account) =>
              !query ||
              (account.name || account.account_name || "")
                .toLowerCase()
                .includes(query.toLowerCase()),
          )
          .map((account) => ({
            id: account.id,
            name: account.name || account.account_name,
          }));
      }
    }

    if (response?.success) {
      const options =
        response?.companies ||
        response?.contacts ||
        response?.sequences ||
        response?.options;

      const updateFilterOptions = (filterId, options) => {
        let filter = props.filtersOptions.find((f) => f.attribute_id === filterId || f.id === filterId);
        if (filter) {
          const transformer =
            optionTransformers[filterId] || optionTransformers.default;
          filter.options = options.map(transformer);
          return;
        }

        for (const f of props.filtersOptions) {
          if (f.withTabs) {
            const tab = f.tabs.find((t) => t.attribute_id === filterId || t.id === filterId);
            if (tab) {
              const transformer =
                optionTransformers[filterId] || optionTransformers.default;
              tab.options = options.map(transformer);
              return;
            }
          }
        }
      };

      updateFilterOptions(filterId, options);
    }
  } catch (error) {
    console.error(`Error searching ${filterId}:`, error);
  } finally {
    if (targetFilter) {
      targetFilter.isLoading = false;
    }
  }
};

const toggleDropdown = (filterId, event) => {
  if (event) {
    event.stopPropagation();
  }

  const tabbedFilter = props.filtersOptions.find(
    (filter) => filter.withTabs && filter.id === filterId,
  );

  if (tabbedFilter) {
    const firstTabId = tabbedFilter.tabs[0]?.id;
    const isAnyTabOpen = tabbedFilter.tabs.some(
      (tab) => tab.id === openDropdownId.value,
    );
    openDropdownId.value = isAnyTabOpen ? null : firstTabId;
    return;
  }

  if (
    openDropdownId.value !== filterId &&
    !openDropdownId.value &&
    ![...dateFilterTypes, "minimum_opened_count"].includes(filterId)
  ) {
    closeDropdown();
  }

  openDropdownId.value = openDropdownId.value === filterId ? null : filterId;

  if (searchableFilterIds.includes(openDropdownId.value)) {
    if (openDropdownId.value === "pages_visited") {
      handleDirectSearch(openDropdownId.value, "");
    } else {
      handleFilterSearch(openDropdownId.value, "");
    }
  }
};

const handleOutsideClick = (event) => {
  const isDatePickerClick =
    event.target.closest(".ant-picker-dropdown") ||
    event.target.closest(".ant-picker") ||
    event.target.closest("#custom_date_filter");

  if (isDatePickerClick) {
    return;
  }

  const dropdowns = document.querySelectorAll(".filter-dropdown");
  let clickedOutside = true;

  dropdowns.forEach((dropdown) => {
    if (dropdown.contains(event.target)) {
      clickedOutside = false;
    }
  });

  if (clickedOutside) {
    openDropdownId.value = null;
    closeDropdown();
  }
};

const exportWithEmailList = async () => {
  if (props.totalSize > 25000) {
    emailToSendExportError.value =
      "Selection exceeds maximum limit of 25,000 rows";
    return;
  }

  if (emailToSendExport.value.trim() === "" && !emailNotRequired.value) {
    emailToSendExportError.value = "Please add an Email";
    return;
  }
  if (!validateEmail(emailToSendExport.value) && !emailNotRequired.value) {
    emailToSendExportError.value = "Please enter valid email";
    return;
  }

  try {
    const payload = {
      export_email: emailToSendExport.value,
    };
    const response = await DashboardService.exportView(
      props.currentView?.id,
      payload,
    );
    if (response.success) {
      await showExportMessage(router, "view", props.currentView?.name);
    } else {
      message.error("Failed to export view");
      console.error("Error occurred while exporting view");
    }
  } catch (error) {
    console.error("Error occurred while exporting view: ", error);
    message.error("Error occurred while exporting view.");
  } finally {
    exporting.value = false;
    showExportViewModal.value = false;
  }
};

const exportWithoutEmailList = async () => {
  try {
    const hide = message.loading("Exporting CSV file...", 0);

    try {
      const response = await exportEmailNotRequiredFunctions[props.source](
        props.filterData,
        props.pageParams,
      );
      hide();

      if (response) {
        downloadCSVFile(response, `meeting_logs_export.csv`);
        message.success("CSV exported successfully!", 2.5);
      }
    } catch (error) {
      hide();

      console.error(
        "An error occurred while exporting email not required view: ",
        error,
      );
      message.error("Failed to export CSV file. Please try again.", 2.5);
    }
  } catch (error) {
    console.error("Error in export process:", error);
    message.error("Export process failed. Please try again.", 2.5);
  }
};

const isFilterApplied = (filter) => {
  if (filter.withTabs) {
    return filter.tabs.some((tab) => {
      const selectedIds = props.filterData[tab.id];
      return Array.isArray(selectedIds)
        ? selectedIds.length > 0
        : !!selectedIds;
    });
  }

  const selectedIds = props.filterData[filter.id];
  return Array.isArray(selectedIds) ? selectedIds.length > 0 : !!selectedIds;
};

const getSelectedNames = (filter) => {
  if (filter.withTabs) {
    const names = [];
    filter.tabs.forEach((tab) => {
      const tabSelectedIds = props.filterData[tab.id];
      if (!tabSelectedIds) return;

      const idsArray = Array.isArray(tabSelectedIds) ? tabSelectedIds : [tabSelectedIds];
      const tabNames = idsArray.map((id) => {
        if (id === -1 || id === "-1") {
          return "Only Me";
        }
        const option = tab.options?.find((opt) => opt.id === id);
        return option ? option.name : id;
      }).filter(Boolean);
      names.push(...tabNames);
    });
    return names;
  }

  const selectedIds = props.filterData[filter.id];
  if (!selectedIds) return [];

  if (selectedIds === -1 || selectedIds === "-1") {
    return ["Only Me"];
  }

  if (
    filter.isTextInput ||
    (typeof selectedIds === "string" &&
      !filter.options?.find((opt) => opt.id === selectedIds))
  ) {
    return [selectedIds.toString()];
  }

  if (
    selectedIds &&
    typeof selectedIds === "object" &&
    "option" in selectedIds
  ) {
    if (
      selectedIds.option === "custom" &&
      selectedIds.start_date &&
      selectedIds.end_date
    ) {
      return [`${selectedIds.start_date} to ${selectedIds.end_date}`];
    }

    const optionName = filter.options?.find(
      (opt) => opt.id === selectedIds.option,
    )?.name;
    return optionName ? [optionName] : [selectedIds.option];
  }

  if (!filter.options) {
    const idsArray = Array.isArray(selectedIds) ? selectedIds : [selectedIds];
    return idsArray.map((id) => {
      if (id === -1 || id === "-1") {
        return "Only Me";
      }
      return id;
    });
  }

  const idsArray = Array.isArray(selectedIds) ? selectedIds : [selectedIds];
  return idsArray.map((id) => {
    if (id === -1 || id === "-1") {
      return "Only Me";
    }
    const option = filter.options.find((opt) => opt.id === id);
    return option ? option.name : id;
  });
};

const getFilterTitle = (filter) => {
  return filter.name;
};

const handleDeleteFilter = (filter) => {
  if (filter.withTabs) {
    filter.tabs.forEach((tab) => {
      emit("filter-changed", {
        filter: tab.id,
        updated_values: null,
      });
    });
  } else {
    emit("filter-changed", {
      filter: filter.id,
      updated_values: null,
    });
  }

  if (openDropdownId.value === filter.id || (filter.withTabs && filter.tabs.some(tab => tab.id === openDropdownId.value))) {
    openDropdownId.value = null;
    closeDropdown();
  }
};

const clearAllSelections = (filter) => {
  if (filter.withTabs) {
    filter.tabs.forEach((tab) => {
      emit("filter-changed", {
        filter: tab.id,
        updated_values: null,
      });
    });
  } else {
    emit("filter-changed", {
      filter: filter.id,
      updated_values: null,
    });
  }
};

const getFilteredOptions = (filter) => {
  if (!filter) return [];

  return filter.options
    ?.map((option) => {
      let isSelected = false;
      const filterValue = props.filterData[filter.id];

      if (
        filterValue &&
        typeof filterValue === "object" &&
        "option" in filterValue
      ) {
        isSelected = filterValue.option === option.id;
      } else if (Array.isArray(filterValue)) {
        isSelected = filterValue.includes(option.id);
      } else {
        isSelected = filterValue === option.id;
      }

      return {
        ...option,
        selected: isSelected,
      };
    })
    .filter((option) => option.name && option.id !== "custom");
};

const handleOptionClick = (option, filter) => {
  closeDropdown();
  selectOption(option, filter);
  if (filter.allowCustomInput) {
    openDropdownId.value = null;
  }
};

const handleCustomDate = (filterId) => {
  openDropdownId.value = filterId;

  const dateFilter = props.filterData[filterId];
  if (dateFilter && dateFilter.start_date && dateFilter.end_date) {
    startDate.value = dayjs(dateFilter.start_date);
    endDate.value = dayjs(dateFilter.end_date);
  } else {
    startDate.value = dayjs().subtract(30, "day");
    endDate.value = dayjs();
  }
  showCustomDateFilter.value = true;
};

const handleDateChange = (data) => {
  startDate.value = data.startDate;
  endDate.value = data.endDate;

  const formattedStartDate = formatISODate(startDate.value, dateTimeFormats[3]);
  const formattedEndDate = formatISODate(endDate.value, dateTimeFormats[3]);
  const date = {
    option: "custom",
    start_date: formattedStartDate,
    end_date: formattedEndDate,
  };
  filterChanged(data.source, date);
};

const handleOpenedFilterChange = (data) => {
  filterChanged(data.source, data.value);
};

const selectOption = (option, filter) => {
  if (!filter) return;

  if (option.id === null) {
    filterChanged(filter.id, null);
    return;
  }

  let selectedIds = props.filterData[filter.id];

  if (dateFilterTypes.includes(filter.id)) {
    if (option.id === (selectedIds?.option || selectedIds)) {
      selectedIds = null;
    } else {
      const formattedStartDate = formatISODate(
        startDate.value,
        dateTimeFormats[3],
      );
      const formattedEndDate = formatISODate(endDate.value, dateTimeFormats[3]);
      selectedIds = {
        option: option.id,
        start_date: formattedStartDate,
        end_date: formattedEndDate,
      };
    }
  } else if (filter.multiSelect) {
    if (!Array.isArray(selectedIds)) {
      selectedIds = selectedIds ? [selectedIds] : [];
    }
    const index = selectedIds.indexOf(option.id);
    if (index > -1) {
      selectedIds.splice(index, 1);
    } else {
      selectedIds.push(option.id);
    }

    if (selectedIds.length === 0) {
      selectedIds = null;
    }
  } else {
    selectedIds = option.id === selectedIds ? null : option.id;
  }

  filterChanged(filter.id, props.entireData ? option : selectedIds);
};

const filterChanged = (filterId, selectedIds) => {
  emit("filter-changed", {
    filter: filterId,
    updated_values: selectedIds,
  });
};

const handleFilterChange = (event) => {
  emit("filter-changed", event);
};

const debouncedFilterSearch = debounce((filterId, query) => {
  handleFilterSearch(filterId, query);
}, 300);

const handleBackButton = (data) => {
  closeDropdown();
  openDropdownId.value = data.source;
  isCustomDate.value = true;
};

const closeDropdown = () => {
  showCustomDateFilter.value = false;
  startDate.value = dayjs();
  endDate.value = dayjs();
};

const handleDirectSearch = (filterId, value) => {
  if (searchableFilterIds.includes(filterId) && filterId !== "pages_visited") {
    debouncedFilterSearch(filterId, value);
  } else {
    emit("filter-search", filterId, value);
  }
};

const handleLoadMore = (filterId) => {
  emit("load-more-options", filterId);
};

watch(openDropdownId, (newValue) => {
  closeDropdown();
});

watch(
  () => props.source,
  async (newSource) => {
    try {
      isValidSchemaEntity.value = await isSchemaEntityType(newSource);
    } catch (error) {
      console.error("Error checking schema entity:", error);
      isValidSchemaEntity.value = false;
    }
  },
);

onMounted(async () => {
  document.addEventListener("click", handleOutsideClick);
  try {
    isValidSchemaEntity.value = await isSchemaEntityType(props.source);
  } catch (error) {
    console.error("Error checking schema entity:", error);
    isValidSchemaEntity.value = false;
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleOutsideClick);
});
</script>

<style scoped>
.dropdown_item {
  position: relative;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  transform: translateZ(0);
}

.dropdown_item:hover {
  background-color: var(--white-opacity-10);
}

.dropdown_item.selected_option {
  background-color: var(--white-opacity-8);
}
</style>